import type {
  DocChatSearchResult,
  DocChatSseArtifactEvent,
  DocChatSseEvent,
  DocChatSseResponsePartialEvent,
  UnifiedDocumentSearchResult,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  DocChatArtifactEvent,
  DocChatCancelledEvent,
  DocChatConversation,
  DocChatErrorEvent,
  DocChatEvent,
  DocChatResponseEvent,
  DocChatResponsePartialEvent,
  DocChatSubmittedQueryAttachment,
  DocChatTagSelection,
  DocChatTurn,
  DocChatTurnStatus,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'

function createConversationEventId(): string {
  return `docchat-event-${crypto.randomUUID()}`
}

const DOC_CHAT_TURN_ID_PREFIX = 'docchat-turn-'

export function createDocChatTurnId(): string {
  return `${DOC_CHAT_TURN_ID_PREFIX}${crypto.randomUUID()}`
}

/** Fresh per send, so safe to hand the backend as the stored turn id. */
export function isClientDocChatTurnId(turnId: string): boolean {
  return turnId.startsWith(DOC_CHAT_TURN_ID_PREFIX)
}

function createSubmittedQueryEvent(
  query: string,
  attachments?: DocChatSubmittedQueryAttachment[]
): DocChatEvent {
  return {
    id: createConversationEventId(),
    type: 'submitted_query',
    query,
    ...(attachments?.length ? { attachments } : {}),
  }
}

function createThinkingEvent(query: string): DocChatEvent {
  return {
    id: createConversationEventId(),
    type: 'thinking',
    query,
  }
}

function createResponseEvent(event: DocChatSearchResult): DocChatResponseEvent {
  return {
    ...event,
    id: createConversationEventId(),
    type: 'response',
    resultCount: event.results.length,
  }
}

function createErrorEvent({
  query,
  siteId,
  conversationId,
  message,
}: {
  query: string
  siteId: string
  conversationId?: string | null
  message: string
}): DocChatErrorEvent {
  return {
    id: createConversationEventId(),
    type: 'error',
    query,
    site_id: siteId,
    conversation_id: conversationId,
    message,
  }
}

function createCancelledEvent(query: string): DocChatCancelledEvent {
  return {
    id: createConversationEventId(),
    type: 'cancelled',
    query,
  }
}

function createArtifactEvent(
  event: DocChatSseArtifactEvent
): DocChatArtifactEvent {
  return {
    id: createConversationEventId(),
    type: 'artifact',
    artifact_id: event.artifact.artifact_id,
    kind: event.artifact.kind,
    title: event.artifact.title,
    revision: event.artifact.revision,
    created: event.artifact.revision <= 1,
    missing_count: event.artifact.content.open_items.length,
    changed_paths: event.changed_paths,
  }
}

function createConversationEvent(
  event: Exclude<DocChatSseEvent, { type: 'started' | 'chart_command' }>
): DocChatEvent {
  if (event.type === 'response') return createResponseEvent(event)
  if (event.type === 'artifact') return createArtifactEvent(event)
  return {
    ...event,
    id: createConversationEventId(),
  }
}

function isTerminalEvent(event: DocChatEvent): boolean {
  return (
    event.type === 'response' ||
    event.type === 'error' ||
    event.type === 'cancelled'
  )
}

function getTurnEvents(turn: DocChatTurn): DocChatEvent[] {
  return Array.isArray(turn.events) ? turn.events : []
}

function isTerminalTurn(turn: DocChatTurn): boolean {
  return getTurnEvents(turn).some(isTerminalEvent)
}

function hasDuplicateThought(turn: DocChatTurn, event: DocChatEvent): boolean {
  return (
    event.type === 'thought' &&
    getTurnEvents(turn).some(
      (currentEvent) =>
        currentEvent.type === 'thought' &&
        currentEvent.message === event.message
    )
  )
}

function eventsWithoutThinking(events: DocChatEvent[]): DocChatEvent[] {
  return events.filter((currentEvent) => currentEvent.type !== 'thinking')
}

function replacePartialEvent(
  events: DocChatEvent[],
  event: DocChatResponsePartialEvent
): DocChatEvent[] {
  const existingIndex = events.findIndex(
    (currentEvent) => currentEvent.type === 'response_partial'
  )
  if (existingIndex < 0) {
    return [...events, event]
  }
  const existing = events[existingIndex]
  if (existing?.type !== 'response_partial') {
    return [...events, event]
  }
  const next = [...events]
  next[existingIndex] = { ...event, id: existing.id }
  return next
}

function replaceArtifactEvent(
  events: DocChatEvent[],
  event: DocChatArtifactEvent
): DocChatEvent[] {
  const existingIndex = events.findIndex(
    (currentEvent) =>
      currentEvent.type === 'artifact' &&
      currentEvent.artifact_id === event.artifact_id
  )
  if (existingIndex < 0) {
    return [...events, event]
  }
  const existing = events[existingIndex]
  if (existing?.type !== 'artifact') {
    return [...events, event]
  }
  if (existing.revision > event.revision) return events
  const next = [...events]
  next[existingIndex] = {
    ...event,
    id: existing.id,
    created: existing.created || event.created,
  }
  return next
}

function appendBackendEventToTurn(
  turn: DocChatTurn,
  event: DocChatEvent
): DocChatTurn {
  if (isTerminalTurn(turn)) return turn
  if (hasDuplicateThought(turn, event)) return turn
  const currentEvents = getTurnEvents(turn)
  const baseEvents =
    currentEvents.length > 0
      ? currentEvents
      : [createSubmittedQueryEvent(turn.query)]
  const withoutThinking = eventsWithoutThinking(baseEvents)
  if (event.type === 'response_partial') {
    return {
      ...turn,
      events: replacePartialEvent(withoutThinking, event),
    }
  }
  if (event.type === 'artifact') {
    return {
      ...turn,
      events: replaceArtifactEvent(withoutThinking, event),
    }
  }
  const withoutPartial =
    event.type === 'response' || event.type === 'error'
      ? withoutThinking.filter(
          (currentEvent) => currentEvent.type !== 'response_partial'
        )
      : withoutThinking
  return {
    ...turn,
    ...(isTerminalEvent(event) ? { pendingResponse: undefined } : {}),
    events: [...withoutPartial, event],
  }
}

export function getDocChatTurnStatus(turn: DocChatTurn): DocChatTurnStatus {
  const events = getTurnEvents(turn)
  if (events.some((event) => event.type === 'response')) return 'complete'
  if (events.some((event) => event.type === 'error')) return 'error'
  if (events.some((event) => event.type === 'cancelled')) return 'cancelled'
  return 'loading'
}

/** The query actually sent to the agent: the typed text plus hidden context. */
export function docChatTurnRequestQuery(turn: DocChatTurn): string {
  return [turn.query, turn.hiddenContext]
    .filter((part): part is string => Boolean(part))
    .join('\n\n')
}

/** Live generation only. Stored prompt-only turns are loading but not in flight. */
export function hasInProgressDocChatTurn(turns: DocChatTurn[]): boolean {
  return turns.some(
    (turn) =>
      getDocChatTurnStatus(turn) === 'loading' &&
      shouldShowDocChatTurnThoughts(turn)
  )
}

/**
 * Whether the active turn was submitted with attachments, judged from the
 * persisted turn metadata so it survives a reload that loses attachment state.
 */
export function activeDocChatTurnHasAttachments(
  conversation: DocChatConversation | undefined
): boolean {
  const activeTurn = conversation?.turns.find(
    (turn) => turn.id === conversation.activeTurnId
  )
  return (
    activeTurn?.events.some(
      (event) =>
        event.type === 'submitted_query' && (event.attachments?.length ?? 0) > 0
    ) ?? false
  )
}

export function hasDocChatConversationEvents(turn: DocChatTurn): boolean {
  return getTurnEvents(turn).length > 0
}

export function createDocChatTurn({
  turnId,
  query,
  attachments,
  hiddenContext,
  tagSelections,
  restoredRunning,
}: {
  turnId: string
  query: string
  attachments?: DocChatSubmittedQueryAttachment[]
  hiddenContext?: string
  tagSelections?: DocChatTagSelection[]
  restoredRunning?: boolean
}): DocChatTurn {
  return {
    id: turnId,
    query,
    ...(hiddenContext ? { hiddenContext } : {}),
    ...(tagSelections?.length ? { tagSelections } : {}),
    ...(restoredRunning ? { restoredRunning: true } : {}),
    events: [
      createSubmittedQueryEvent(query, attachments),
      createThinkingEvent(query),
    ],
  }
}

export const DOCUMENT_CHAT_PENDING_TURN_ID = 'pending-user-turn'

function pendingUserTurn(query: string, turnId: string): DocChatTurn {
  return {
    id: turnId,
    query,
    events: [
      {
        id: `${turnId}-submitted`,
        type: 'submitted_query',
        query,
      },
    ],
  }
}

/**
 * Prompt-only history for a stored agent user query with no snapshot. No
 * thinking event and no activeTurnId — those would look like a live request.
 */
export function conversationFromPendingUserQuery({
  conversationId,
  query,
}: {
  conversationId: string
  query: string
}): DocChatConversation {
  return {
    id: conversationId,
    turns: [pendingUserTurn(query, DOCUMENT_CHAT_PENDING_TURN_ID)],
  }
}

export function createCompletedDocChatTurn({
  turnId,
  result,
}: {
  turnId: string
  result: DocChatSearchResult
}): DocChatTurn {
  return {
    id: turnId,
    query: result.query,
    events: [
      createSubmittedQueryEvent(result.query),
      createResponseEvent(result),
    ],
  }
}

export const DOC_CHAT_SEARCH_FAILED_FALLBACK = 'Search failed. Try again.'

export function createErroredDocChatTurn({
  turnId,
  query,
  siteId,
  message = DOC_CHAT_SEARCH_FAILED_FALLBACK,
}: {
  turnId: string
  query: string
  siteId: string
  message?: string
}): DocChatTurn {
  return {
    id: turnId,
    query,
    events: [
      createSubmittedQueryEvent(query),
      createErrorEvent({
        query,
        siteId,
        message,
      }),
    ],
  }
}

export function conversationWithRunningTurn({
  conversation,
  query,
  turnId,
}: {
  conversation: DocChatConversation
  query: string
  turnId: string
}): DocChatConversation {
  const running = createDocChatTurn({
    turnId,
    query,
    restoredRunning: true,
  })
  const turns = conversation.turns.filter(
    (turn) => turn.id !== DOCUMENT_CHAT_PENDING_TURN_ID
  )
  const already = turns.some((turn) => turn.id === turnId)
  return {
    ...conversation,
    activeTurnId: turnId,
    turns: already ? turns : [...turns, running],
  }
}

export function conversationWithFailedTurn({
  conversation,
  query,
  turnId,
  siteId,
  message,
}: {
  conversation: DocChatConversation
  query: string
  turnId: string
  siteId: string
  message: string
}): DocChatConversation {
  const failed = createErroredDocChatTurn({
    turnId,
    query,
    siteId,
    message,
  })
  const turns = conversation.turns.filter(
    (turn) => turn.id !== DOCUMENT_CHAT_PENDING_TURN_ID && turn.id !== turnId
  )
  return { ...conversation, turns: [...turns, failed] }
}

export function appendDocChatStreamEvent({
  conversation,
  streamEvent,
  siteId,
}: {
  conversation: DocChatConversation
  streamEvent: DocChatSseEvent
  siteId: string
}): DocChatConversation {
  if (streamEvent.type === 'started') {
    return {
      ...conversation,
      id: streamEvent.conversation_id,
      activeTurnId: conversation.activeTurnId,
    }
  }
  if (streamEvent.type === 'chart_command') return conversation
  const activeTurnId = conversation.activeTurnId
  if (!activeTurnId) return conversation
  if (streamEvent.site_id !== siteId) return conversation

  let changed = false
  const conversationEvent = createConversationEvent(streamEvent)
  const turns = conversation.turns.map((turn) => {
    if (turn.id !== activeTurnId) return turn
    if (docChatTurnRequestQuery(turn) !== streamEvent.query) return turn
    const updatedTurn = appendBackendEventToTurn(turn, conversationEvent)
    if (updatedTurn === turn) return turn
    changed = true
    return updatedTurn
  })

  if (!changed) return conversation

  const conversationId = streamEvent.conversation_id ?? conversation.id
  return {
    ...conversation,
    ...(conversationId ? { id: conversationId } : {}),
    turns,
  }
}

export function receiveDocChatResponse({
  conversation,
  response,
  siteId,
}: {
  conversation: DocChatConversation
  response: DocChatSearchResult
  siteId: string
}): DocChatConversation {
  if (response.site_id !== siteId) return conversation
  let changed = false
  const turns = conversation.turns.map((turn) => {
    if (
      turn.id !== conversation.activeTurnId ||
      isTerminalTurn(turn) ||
      docChatTurnRequestQuery(turn) !== response.query
    ) {
      return turn
    }
    changed = true
    return { ...turn, pendingResponse: createResponseEvent(response) }
  })
  return changed
    ? { ...conversation, id: response.conversation_id, turns }
    : conversation
}

export function markDocChatTurnError({
  conversation,
  turnId,
  siteId,
}: {
  conversation: DocChatConversation
  turnId: string
  siteId: string
}): DocChatConversation {
  let changed = false
  const turns = conversation.turns.map((turn) => {
    if (turn.id !== turnId) return turn
    const errorEvent = createErrorEvent({
      query: turn.query,
      siteId,
      conversationId: conversation.id,
      message: DOC_CHAT_SEARCH_FAILED_FALLBACK,
    })
    const updatedTurn = appendBackendEventToTurn(turn, errorEvent)
    if (updatedTurn === turn) return turn
    changed = true
    return updatedTurn
  })

  if (!changed) return conversation
  return {
    ...conversation,
    turns,
  }
}

export function markDocChatTurnRestoredRunning({
  conversation,
  turnId,
}: {
  conversation: DocChatConversation
  turnId: string
}): DocChatConversation {
  let changed = false
  const turns = conversation.turns.map((turn) => {
    if (turn.id !== turnId || turn.restoredRunning) return turn
    changed = true
    return { ...turn, restoredRunning: true }
  })
  if (!changed) return conversation
  return { ...conversation, turns }
}

export function markDocChatTurnCancelled({
  conversation,
  turnId,
}: {
  conversation: DocChatConversation
  turnId: string
}): DocChatConversation {
  let changed = false
  const turns = conversation.turns.map((turn) => {
    if (turn.id !== turnId) return turn
    const updatedTurn = appendBackendEventToTurn(
      turn,
      createCancelledEvent(turn.query)
    )
    if (updatedTurn === turn) return turn
    changed = true
    return updatedTurn
  })

  if (!changed) return conversation
  return {
    ...conversation,
    turns,
  }
}

export function shouldShowDocChatTurnThoughts(turn: DocChatTurn): boolean {
  return turn.events.some(
    (event) =>
      event.type === 'thinking' ||
      event.type === 'thought' ||
      event.type === 'response' ||
      event.type === 'response_partial'
  )
}

/**
 * Leftover client loading turns with no live request become a failed retry
 * row. A restored server-running turn stays spinning while it is still the
 * active turn — the worker is still going, so this is not Search failed.
 * An older restored spinner is Stopped when a follow-up send starts.
 */
export function resolveAbandonedDocChatTurns({
  conversation,
  siteId,
  isTurnInFlight,
}: {
  conversation: DocChatConversation
  siteId: string
  isTurnInFlight: (turnId: string) => boolean
}): DocChatConversation {
  return conversation.turns.reduce((current, turn) => {
    if (getDocChatTurnStatus(turn) !== 'loading') return current
    if (!shouldShowDocChatTurnThoughts(turn)) return current
    if (isTurnInFlight(turn.id)) return current
    if (turn.restoredRunning) {
      if (conversation.activeTurnId === turn.id) return current
      return markDocChatTurnCancelled({
        conversation: current,
        turnId: turn.id,
      })
    }
    return markDocChatTurnError({
      conversation: current,
      turnId: turn.id,
      siteId,
    })
  }, conversation)
}

/** Latest artifact marker for an id across the conversation, newest turn first. */
export function findLatestArtifactEvent(
  conversation: DocChatConversation | undefined,
  artifactId: string
): DocChatArtifactEvent | undefined {
  if (!conversation) return undefined
  for (let index = conversation.turns.length - 1; index >= 0; index -= 1) {
    const events = getTurnEvents(conversation.turns[index])
    for (let eventIndex = events.length - 1; eventIndex >= 0; eventIndex -= 1) {
      const event = events[eventIndex]
      if (event.type === 'artifact' && event.artifact_id === artifactId) {
        return event
      }
    }
  }
  return undefined
}

/** Highest-revision artifact marker for an id across every open tab's chat. */
export function findLatestArtifactEventInTabs(
  tabs: readonly DocumentTab[],
  artifactId: string
): DocChatArtifactEvent | undefined {
  let latest: DocChatArtifactEvent | undefined
  for (const tab of tabs) {
    for (const item of tab.history) {
      if (item.type !== 'search') continue
      const event = findLatestArtifactEvent(
        item.searchState.docChat?.conversation,
        artifactId
      )
      if (event && (!latest || event.revision > latest.revision)) {
        latest = event
      }
    }
  }
  return latest
}

export function settleDocChatConversationOnRender({
  conversation,
  shouldStartStream,
  siteId,
  isTurnInFlight,
}: {
  conversation: DocChatConversation
  shouldStartStream: boolean
  siteId: string
  isTurnInFlight: (turnId: string) => boolean
}): DocChatConversation {
  if (!shouldStartStream) return conversation
  return resolveAbandonedDocChatTurns({
    conversation,
    siteId,
    isTurnInFlight,
  })
}

export function documentSearchResultFromPartial(
  event: DocChatSseResponsePartialEvent
): UnifiedDocumentSearchResult | undefined {
  if (event.results.length === 0) return undefined
  return {
    query: event.query,
    site_id: event.site_id,
    weights: {
      lexical_weight: 0,
      semantic_weight: 0,
    },
    results: event.results,
  }
}

export function documentSearchResultFromActiveTurn(
  conversation: DocChatConversation | undefined
): UnifiedDocumentSearchResult | undefined {
  const activeTurnId = conversation?.activeTurnId
  if (!conversation || !activeTurnId) return undefined
  const activeTurn = conversation.turns.find((turn) => turn.id === activeTurnId)
  if (!activeTurn) return undefined
  const partial = getTurnEvents(activeTurn).find(
    (event): event is DocChatResponsePartialEvent =>
      event.type === 'response_partial'
  )
  if (!partial) return undefined
  return documentSearchResultFromPartial(partial)
}

export function documentSearchResultFromActiveTurnResponse(
  conversation: DocChatConversation | undefined
): DocChatSearchResult | undefined {
  const activeTurnId = conversation?.activeTurnId
  if (!conversation || !activeTurnId) return undefined
  const activeTurn = conversation.turns.find((turn) => turn.id === activeTurnId)
  if (!activeTurn) return undefined
  const response =
    getTurnEvents(activeTurn).find(
      (event): event is DocChatResponseEvent => event.type === 'response'
    ) ?? activeTurn.pendingResponse
  if (response === undefined || response.results === undefined) {
    return undefined
  }
  return {
    type: response.type,
    query: response.query,
    site_id: response.site_id,
    conversation_id: response.conversation_id,
    final_response: response.final_response,
    response: response.response,
    response_segments: response.response_segments,
    cmms: response.cmms,
    metadata: response.metadata,
    results: response.results,
  }
}
